import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { caseTypes } from '../mock';
import { PhoneCall, CheckCircle, CreditCard, Smartphone, Building2 } from 'lucide-react';
import { EMAILJS_CONFIG } from '../emailConfig';

const ConsultationForm = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    caseType: '',
    message: ''
  });
  
  // Initialize EmailJS with private key for strict mode
  useEffect(() => {
    emailjs.init({
      publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
      privateKey: EMAILJS_CONFIG.PRIVATE_KEY
    });
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare email template parameters - using standard EmailJS variable names
      const templateParams = {
        to_name: 'Advocate Imran Pathan',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        case_type: formData.caseType,
        message: formData.message,
        reply_to: formData.email
      };

      // Send email via EmailJS (init already done with private key)
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.CONSULTATION_TEMPLATE_ID,
        templateParams
      );
      
      console.log('EmailJS Response:', response);

      toast({
        title: "Success",
        description: t.form_success,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        caseType: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast({
        title: "Error",
        description: error?.text || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-16 bg-gradient-to-br from-blue-950 to-blue-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Free Consultation Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-3 bg-green-500 text-white px-6 py-3 rounded-full mb-4 shadow-xl">
              <PhoneCall className="h-6 w-6" />
              <span className="font-bold text-lg">
                {t.free_consultation || 'FREE CONSULTATION AVAILABLE'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>{t.consultation_title}</h2>
            <p className="text-gray-300 mb-4">{t.consultation_subtitle}</p>
            
            {/* Benefits */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="flex items-center space-x-2 text-white">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-sm">{t.no_obligation || 'No Obligation'}</span>
              </div>
              <div className="flex items-center space-x-2 text-white">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-sm">{t.confidential || 'Confidential'}</span>
              </div>
              <div className="flex items-center space-x-2 text-white">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-sm">{t.professional_advice || 'Professional Advice'}</span>
              </div>
            </div>

            {/* Online Payment Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <CreditCard className="h-5 w-5 text-amber-400" />
                <span className="text-amber-400 font-semibold">{t.online_payment || 'Online Payment Accepted'}</span>
              </div>
              <p className="text-gray-300 text-sm text-center mb-3">
                {t.payment_note || 'Initial consultation is FREE. For further professional services, payments can be made online.'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center space-x-2 text-white bg-white/10 px-3 py-1.5 rounded-full">
                  <Smartphone className="h-4 w-4 text-green-400" />
                  <span className="text-xs">UPI</span>
                </div>
                <div className="flex items-center space-x-2 text-white bg-white/10 px-3 py-1.5 rounded-full">
                  <Building2 className="h-4 w-4 text-blue-400" />
                  <span className="text-xs">{t.bank_transfer || 'Bank Transfer'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">{t.form_name}</label>
                  <Input 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    placeholder={t.form_name}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">{t.form_email}</label>
                    <Input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleInputChange} 
                      required 
                      placeholder={t.form_email}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">{t.form_phone}</label>
                    <Input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleInputChange} 
                      required 
                      placeholder={t.form_phone}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">{t.form_case_type}</label>
                  <Select value={formData.caseType} onValueChange={(value) => setFormData(prev => ({ ...prev, caseType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t.form_case_type_placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {caseTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">{t.form_message}</label>
                  <Textarea 
                    name="message" 
                    value={formData.message} 
                    onChange={handleInputChange} 
                    required 
                    rows={4}
                    placeholder={t.form_message}
                  />
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? t.form_submitting : t.form_submit}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ConsultationForm;
